import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './tutor.css';
import { unfolder, urlValue } from '../../../lib';

const Tutor = ({ steps = [], viewKey }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [checking, setChecking] = useState(true);

    // Nuevo: pasos realmente válidos según DOM/visibilidad
    const [validSteps, setValidSteps] = useState([]);
    // Nuevo: solo mostrar si BD dice que no está completado
    const canShowRef = useRef(false);
    const dismissedRef = useRef(false);

    const getAuth = () => {
        try {
            const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey')?.token;
            const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken
            const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey')?.id;
            return { token, id, csrfToken };
        } catch (_) {
            return { token: null, id: null, csrfToken: null };
        }
    };

    // ---------- Utilidades de visibilidad ----------
    const isElementUsable = (el) => {
        if (!el) return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) {
            return false;
        }
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    };

    const recalcValidSteps = () => {
        if (dismissedRef.current) {
            setIsVisible(false);
            return;
        }

        const filtered = (steps || []).filter((s) => {
            if (!s?.elementId) return false;
            const el = document.getElementById(s.elementId);
            return isElementUsable(el);
        });

        setValidSteps(filtered);

        // Si BD permite mostrar, solo mostrar si hay al menos 1 paso válido
        const shouldShow = canShowRef.current && !dismissedRef.current && filtered.length > 0;
        setIsVisible(shouldShow);

        // Reset del índice si quedó fuera de rango
        if (filtered.length > 0 && currentStep >= filtered.length) {
            setCurrentStep(0);
        }
    };

    // ---------- Consulta a BD para ver si ya completó la vista ----------
    useEffect(() => {
        const checkFromDB = async () => {
            if (!viewKey || steps.length === 0) {
                setChecking(false);
                return;
            }
            const { token, id } = getAuth();
            if (!token || !id) {
                canShowRef.current = false;
                setChecking(false);
                setIsVisible(false);
                return;
            }
            try {
                const resp = await axios({
                    method: 'get',
                    url: `${urlValue}/studio/tutor/${id}/${encodeURIComponent(viewKey)}`,
                    headers: { user: id, 'access-token': token },
                    params: { timestamp: Date.now(), app: 'studio', user_id: id },
                    timeout: 5000
                });
                const completed = !!resp?.data?.completed;
                canShowRef.current = !completed;
            } catch (error) {
                console.error('Tutor check error:', error);
                // En errores de red, permitimos mostrar si hay pasos válidos
                canShowRef.current = true;
            } finally {
                setChecking(false);
                // Importante: no decidimos visibilidad aquí; se decide en recalcValidSteps()
                recalcValidSteps();
            }
        };
        checkFromDB();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steps, viewKey]);

    // ---------- Recalcular cuando cambian steps ----------
    useEffect(() => {
        if (!checking) recalcValidSteps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steps, checking]);

    // ---------- Observar cambios del DOM (roles, permisos, lazy render) ----------
    useEffect(() => {
        const observer = new MutationObserver(() => recalcValidSteps());
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden', 'id']
        });
        window.addEventListener('resize', recalcValidSteps);
        window.addEventListener('scroll', recalcValidSteps, { passive: true });
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', recalcValidSteps);
            window.removeEventListener('scroll', recalcValidSteps);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- Marcar completado ----------
    const markCompletedInDB = async () => {
        const { token, id, csrfToken } = getAuth();
        if (!token || !id || !csrfToken) return;

        try {
            await axios({
                method: 'post',
                url: `${urlValue}/studio/tutor`,
                headers: {
                    user: id,
                    'access-token': token,
                    'X-CSRF-Token': csrfToken,
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                data: {
                    app: 'studio',
                    user_id: id,
                    view_key: viewKey
                },
                timeout: 5000
            });
        } catch (error) {
            console.error('Tutor markCompleted error:', error);
        }
    };

    const finishTutorial = () => {
        dismissedRef.current = true;
        canShowRef.current = false;
        setIsVisible(false);
        setValidSteps([]);
        markCompletedInDB();
    };

    const handleNext = () => {
        if (currentStep < validSteps.length - 1) setCurrentStep((s) => s + 1);
        else finishTutorial();
    };

    // ---------- Auto-avance si el elemento del paso desaparece ----------
    useEffect(() => {
        if (!isVisible || validSteps.length === 0) return;
        const cur = validSteps[currentStep];
        const el = cur?.elementId ? document.getElementById(cur.elementId) : null;
        if (!isElementUsable(el)) {
            if (currentStep < validSteps.length - 1) setCurrentStep((s) => s + 1);
            else setIsVisible(false); // no marcamos completado para no “quemar” el tutorial
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validSteps, currentStep, isVisible]);

    if (checking || !isVisible || validSteps.length === 0) return null;

    const current = validSteps[currentStep];
    const targetElement = document.getElementById(current.elementId);
    const rect = targetElement?.getBoundingClientRect();

    // Si por alguna razón no hay rect en este render, no mostramos nada (evita romper UI)
    if (!rect) return null;

    const margin = 10;
    const tooltipWidth = 300;
    const tooltipHeight = 100;
    const windowWidth = window.innerWidth;
    let top = rect.top + window.scrollY;
    let left = rect.left + window.scrollX + rect.width + margin;
    if (left + tooltipWidth > windowWidth) {
        left = rect.left + window.scrollX - tooltipWidth - margin;
    }

    return (
        <div className="tutor-overlay">
            <div className="tutor-backdrop"></div>

            <div
                className="tutor-highlight"
                style={{
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    height: rect.height
                }}
            />

            <div
                className="tutor-box"
                style={{
                    position: 'absolute',
                    top,
                    left,
                    width: `${tooltipWidth}px`,
                    background: 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    boxShadow: '0 0 15px rgba(0,0,0,0.4)',
                    zIndex: 10001,
                    pointerEvents: 'auto'
                }}
            >
                <p>{current.text}</p>
                <div className="tutor-buttons">
                    <button onClick={finishTutorial} style={{display: currentStep !== validSteps.length - 1 ? 'block' : 'none'}}>Skip</button>
                    <button onClick={handleNext}>{currentStep === validSteps.length - 1 ? 'Finish' : 'Next'}</button>
                </div>
            </div>
        </div>
    );
};

export default Tutor;
